


























































































































































import { Component, Prop, Vue } from 'vue-property-decorator'
import { Action, Getter, State } from 'vuex-class'

import {
  CustomerInfo,
  InvoicePaymentMethodChip,
  NetworkErrorEmptyState,
  OrderItemTile,
  PrintReceipt,
  VsLoading
} from '@/components'

import {
  ListHeader,
  ListItem,
  MobileLayout,
  SkeletonBox,
  VxCard
} from '@/layouts/components'

import {
  ActionButton,
  AsyncState,
  Invoice,
  InvoiceStatus,
  Order,
  OrderItem,
  OrderPayment,
  OrderPaymentPayload,
  OrderPaymentStatus,
  OrderStatus,
  OrderType,
  PaymentMethodEnum,
  FilazeroPaymentMethod
} from '@/models'

import { RelationMap } from '@/store/models'
import { RootState } from '@/store/state'
import { InvoiceOrders } from '@/store/ordering/state'
import { isFood } from '@/utils'

@Component({
  components: {
    CustomerInfo,
    InvoicePaymentMethodChip,
    ListHeader,
    ListItem,
    MobileLayout,
    NetworkErrorEmptyState,
    OrderItemTile,
    PrintReceipt,
    SkeletonBox,
    VsLoading,
    VxCard
  }
})
export default class InvoiceDetails extends Vue {
  @Prop({ type: Number, required: true })
  readonly invoiceId!: number

  loadState = new AsyncState()
  closeState = new AsyncState()
  orderPaymentState = new AsyncState()

  get buttons (): ActionButton[] {
    return [
      {
        icon: 'print',
        color: 'dark',
        handler: window.print,
        isDisabled: this.invoice === null
      }
    ]
  }

  get invoice (): Invoice | null {
    return this.invoiceById(this.invoiceId)
  }

  get invoiceOrders (): InvoiceOrders | undefined {
    return this.invoicesOrders[this.invoiceId]
  }

  get orders (): Order[] {
    return this.ordersByInvoiceId(this.invoiceId)
  }

  get orderPayment (): OrderPayment | null {
    return this.orderPaymentByInvoiceId(this.invoiceId)
  }

  get lastUpdateTime () {
    if (this.invoice !== null) {
      return this.lastUpdate(this.invoice)
    }
  }

  get didPay () {
    return this.invoice?.status === InvoiceStatus.CLOSED
  }

  get received () {
    if (this.invoice !== null && this.showChange) {
      return this.invoice.change !== null
        ? this.invoice.change
        : this.invoice.total
    }

    return 0
  }

  get showChange () {
    return this.invoice !== null && this.invoice.status !== InvoiceStatus.OPEN
  }

  get showDeliveryFee () {
    return this.orders[0]?.type === OrderType.DELIVERY
  }

  get isCanceled () {
    return this.orders[0]?.status === OrderStatus.CANCELED
  }

  get locations () {
    return this.invoice
      ? this.invoiceLocations(this.invoice)
      : null
  }

  get title () {
    if (this.invoice !== null) {
      return isFood() ? `Comanda #${this.invoice.code}` : `Compra #${this.invoice.code}`
    }

    return this.loadState.isPending
      ? null
      : 'Detalhes da comanda'
  }

  get isOrderPaymentClosed () {
    return (this.orderPayment !== null && this.orderPayment.status === OrderPaymentStatus.COMPLETED) || (this.invoice !== null && this.invoice.paymentMethod === PaymentMethodEnum.ONLINE)
  }

  @State((state: RootState) => state.ordering.invoices.orders)
  readonly invoicesOrders!: RelationMap<InvoiceOrders>

  @Getter('ordering/invoiceById')
  readonly invoiceById!: (id: number) => Invoice | null

  @Getter('ordering/invoiceLastUpdate')
  readonly lastUpdate!: (invoice: Invoice) => Date

  @Getter('ordering/invoiceLocations')
  readonly invoiceLocations!: (invoice: Invoice) => string[] | null

  @Getter('ordering/ordersByInvoiceId')
  readonly ordersByInvoiceId!: (id: number) => Order[]

  @Getter('ordering/orderPaymentByInvoiceId')
  readonly orderPaymentByInvoiceId!: (id: number) => OrderPayment

  @Action('ordering/loadInvoiceOrders')
  loadInvoiceOrders!: (id: number) => Promise<void>

  @Action('ordering/closeInvoice')
  closeInvoice!: (id: number) => Promise<void>

  @Action('ordering/loadOrderPayment')
  loadOrderPayment!: (id: number) => Promise<void>

  @Action('ordering/informPayment')
  informPayment!: (payload: OrderPaymentPayload) => Promise<void>

  async created () {
    if (
      this.invoice === null ||
      this.invoiceOrders === undefined ||
      !this.invoiceOrders.didLoadAll
    ) {
      await this.reload()

      this.checkPaymentMethod()
    } else {
      this.loadState.touch()
    }
  }

  async reload () {
    try {
      await this.loadState.handle(
        this.loadInvoiceOrders(this.invoiceId)
      )
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  async handleCloseInvoice () {
    try {
      if (this.orderPayment !== null) {
        if (!this.isOrderPaymentClosed) {
          const payload: OrderPaymentPayload = {
            id: this.orderPayment.id,
            method: this.setMethod(),
            ownerId: this.orderPayment.ownerId
          }

          this.informPayment(payload)
        }
      }

      await this.closeState.handle(
        this.closeInvoice(this.invoiceId)
      )

      this.$vs.notify({
        title: 'Pagamento confirmado.',
        color: 'success',
        fullWidth: true
      })
    } catch (e) {
      this.$vs.notify({
        title: 'Não foi possível confirmar o pagamento.',
        color: 'danger',
        fullWidth: true
      })

      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  async checkPaymentMethod () {
    try {
      if (this.invoice !== null && this.invoice.paymentMethod !== null) {
        await this.orderPaymentState.handle(
          this.loadOrderPayment(this.invoiceId)
        )
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  setMethod (): FilazeroPaymentMethod {
    if (this.invoice !== null && this.invoice.paymentMethod === PaymentMethodEnum.CASH) {
      return FilazeroPaymentMethod.MONEY
    } else if (this.invoice !== null && this.invoice.paymentMethod === PaymentMethodEnum.CREDIT_CARD) {
      return FilazeroPaymentMethod.CREDIT_CARD
    } else {
      return FilazeroPaymentMethod.DEBIT_CARD
    }
  }

  itemKey (order: Order, item: OrderItem, index: number) {
    const repeated = order.items.filter((i) => i.id === item.id).length > 1

    return `${order.id}_${item.id}${repeated ? `_${index}` : ''}`
  }
}
